/**
 * LibraryView + ForkModal — saved-packs library, trash tab & fork-to-calendar.
 *
 * Extracted from App.js during the P1 refactor. Zero behavioural changes.
 * The MONTH_ORDER constant and API base URL are threaded in as props so
 * this view stays framework-agnostic.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import { downloadFromPath, fetchPackForWorkspace } from "../lib/beaconUtils";
import { ScoreInfo } from "./ScoreInfo.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/beacon`;

const MONTH_ORDER = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function reviewTone(review) {
  const low = Math.min(review.quality, review.compliance);
  if (low >= 8) return "is-good";
  if (low >= 6) return "is-ok";
  return "is-poor";
}

function ScoreBadge({ pack }) {
  const cur = pack.review;
  const prev = pack.rebuilt_from?.review;
  if (!cur && !prev) return null;
  if (!cur) {
    return (
      <span className="beacon-review-badge is-prev"
        data-testid={`pack-score-${pack.pack_id}`}
        title={`Rebuilt from a pack scored quality ${prev.quality}/10, compliance ${prev.compliance}/10 — not re-scored yet`}>
        was Q{prev.quality} · C{prev.compliance}
      </span>
    );
  }
  const tip = [
    `Luma review — quality ${cur.quality}/10, compliance ${cur.compliance}/10`,
    prev ? `(up from Q${prev.quality} · C${prev.compliance} before the rebuild)` : "",
    cur.verdict || "",
  ].filter(Boolean).join(" · ");
  return (
    <span className={`beacon-review-badge ${reviewTone(cur)}`}
      data-testid={`pack-score-${pack.pack_id}`} title={tip}>
      ✦ Q{prev ? `${prev.quality}→` : ""}{cur.quality} · C{prev ? `${prev.compliance}→` : ""}{cur.compliance}
    </span>
  );
}

export function LibraryView({ onOpenPack, onReviewPack }) {
  const [packs, setPacks] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(""); // "" = all sites
  const [tab, setTab] = useState("live"); // "live" | "trash"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forking, setForking] = useState(null); // pack_id being forked
  const [reviewJob, setReviewJob] = useState(null); // {status,total,done}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [packsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/packs${tab === "trash" ? "/trash" : ""}`, {
          params:
            tab === "live" && selectedSiteId
              ? { site_id: selectedSiteId }
              : undefined,
        }),
        // Sites list is derived from live packs only — Trash tab keeps
        // the same filter but hides it under the Trash header.
        axios.get(`${API}/packs/sites`),
      ]);
      setPacks(packsRes.data.packs);
      setSites(sitesRes.data.sites || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, selectedSiteId]);

  useEffect(() => { load(); }, [load]);

  const deletePack = async (packId) => {
    if (!window.confirm("Move this pack to the Trash? You can restore it any time.")) return;
    try {
      await axios.delete(`${API}/packs/${packId}`);
      await load();
    } catch (e) { alert(`Delete failed: ${e.message}`); }
  };

  const restorePack = async (packId) => {
    try {
      await axios.post(`${API}/packs/${packId}/restore`);
      await load();
    } catch (e) { alert(`Restore failed: ${e.message}`); }
  };

  const purgePack = async (packId) => {
    if (!window.confirm(
      "Permanently delete this pack? This cannot be undone.",
    )) return;
    try {
      await axios.delete(`${API}/packs/${packId}/permanent`);
      await load();
    } catch (e) { alert(`Permanent delete failed: ${e.message}`); }
  };

  const emptyTrash = async () => {
    if (!window.confirm(
      `Permanently delete all ${packs.length} pack${packs.length === 1 ? "" : "s"} in the Trash? This cannot be undone.`,
    )) return;
    try {
      await axios.delete(`${API}/packs/trash/empty`);
      await load();
    } catch (e) { alert(`Empty trash failed: ${e.message}`); }
  };

  const openPack = async (packId) => {
    try {
      onOpenPack(await fetchPackForWorkspace(packId));
    } catch (e) { alert(`Open failed: ${e.message}`); }
  };

  const isTrash = tab === "trash";

  // Review-all batch job: kick off + poll until done, then reload tiles.
  const pollRef = useRef(null);
  const pollStatus = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/luma/review-all/status`);
        if (r.data.status === "running") {
          setReviewJob(r.data);
        } else {
          clearInterval(pollRef.current);
          setReviewJob(null);
          await load();
          if (r.data.status === "error") {
            alert("Review-all timed out — packs scored so far kept their badges. Try again for the rest.");
          }
        }
      } catch { /* transient poll failure — keep polling */ }
    }, 4000);
  }, [load]);

  useEffect(() => {
    axios.get(`${API}/luma/review-all/status`)
      .then((r) => {
        if (r.data.status === "running") {
          setReviewJob(r.data);
          pollStatus();
        }
      })
      .catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [pollStatus]);

  const reviewAll = async () => {
    try {
      const r = await axios.post(`${API}/luma/review-all`, {
        plan_id: "default",
      });
      if (r.data.status === "running") {
        setReviewJob(r.data);
        pollStatus();
      }
    } catch (e) { alert(`Review all failed: ${e.message}`); }
  };

  const unreviewedCount = packs.filter((p) => !p.review).length;

  return (
    <section className="beacon-library" data-testid="library-view">
      <div className="beacon-sticky-head" data-testid="library-sticky-head">
      <div className="beacon-plan-header">
        <div>
          <div className="beacon-card-eyebrow">
            LIBRARY · {isTrash ? "TRASH" : "SAVED PACKS"}
          </div>
          <h2 className="beacon-card-title">
            {isTrash
              ? "Recently deleted packs."
              : "Everything you\u2019ve built so far."}
          </h2>
          <p className="beacon-card-lede">
            {isTrash ? (
              <>Restore anything you deleted by accident, or purge it permanently.</>
            ) : (
              <>
                Every generated pack lives here — re-open it in the workspace,
                move it to the Trash, or fork it onto the calendar as a
                one-off replacement for any week.
              </>
            )}
          </p>
        </div>
        <button type="button" className="beacon-secondary-btn" onClick={load}
          data-testid="library-refresh">Refresh</button>
      </div>

      <div className="beacon-library-toolbar" data-testid="library-toolbar">
        <div
          className="beacon-library-tabs"
          role="tablist"
          data-testid="library-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "live"}
            data-testid="library-tab-live"
            className={`beacon-library-tab${tab === "live" ? " is-active" : ""}`}
            onClick={() => setTab("live")}
          >Saved</button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "trash"}
            data-testid="library-tab-trash"
            className={`beacon-library-tab${tab === "trash" ? " is-active" : ""}`}
            onClick={() => setTab("trash")}
          >Trash</button>
        </div>

        {isTrash && packs.length > 0 && (
          <button type="button"
            className="beacon-secondary-btn beacon-admin-btn-danger"
            data-testid="library-empty-trash"
            onClick={emptyTrash}
            title="Permanently delete everything in the Trash">
            ✕ Delete all ({packs.length})
          </button>
        )}
        {!isTrash && packs.length > 0 && (
          <button type="button" className="beacon-secondary-btn"
            data-testid="library-doc-register"
            title="Download the ISO 45001 document register (Doc ID, version, effective date, authoriser) as CSV"
            onClick={() => downloadFromPath(
              "/api/beacon/packs/register?plan_id=default&format=csv",
              "beacon-doc-register.csv",
            )}>
            ⎙ Doc register
          </button>
        )}
        {!isTrash && (
          <span className="beacon-library-reviewall-wrap">
            {(reviewJob || unreviewedCount > 0) && (
              <button type="button" className="beacon-secondary-btn"
                data-testid="library-review-all"
                disabled={!!reviewJob}
                onClick={reviewAll}
                title="Ask Luma to score every unreviewed pack for quality and compliance">
                {reviewJob
                  ? `✦ Reviewing ${Math.min(reviewJob.done + 1, reviewJob.total)}/${reviewJob.total}…`
                  : `✦ Review all (${unreviewedCount})`}
              </button>
            )}
            <ScoreInfo align="left" />
          </span>
        )}
        {!isTrash && sites.length > 0 && (
          <div className="beacon-library-site-filter">
            <label className="beacon-tiny-label" htmlFor="library-site-select">
              Site
            </label>
            <select
              id="library-site-select"
              className="beacon-input beacon-library-site-select"
              data-testid="library-site-filter"
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
            >
              <option value="">All sites ({sites.reduce((a, s) => a + s.count, 0)})</option>
              {sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>
                  {s.site_name} ({s.count})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      </div>

      {loading && (
        <div className="beacon-tab-loading"><span className="beacon-spinner beacon-spinner-dark" />Loading library…</div>
      )}
      {error && (<div className="beacon-tab-error">{error}</div>)}
      {!loading && packs.length === 0 && (
        <div className="beacon-library-empty" data-testid="library-empty">
          {isTrash ? (
            <>Trash is empty. Deleted packs show up here for restoring or permanent removal.</>
          ) : selectedSiteId ? (
            <>No packs saved for this site yet. Try selecting &ldquo;All sites&rdquo; or generate one from Quick Comms.</>
          ) : (
            <>No packs yet. Generate one from Quick Comms &mdash; it&rsquo;ll appear here automatically.</>
          )}
        </div>
      )}

      <div className="beacon-library-grid">
        {packs.map((p) => (
          <div key={p.pack_id} className="beacon-library-tile"
            data-testid={`pack-tile-${p.pack_id}`}>
            <div className="beacon-library-tile-kind">
              {p.source_kind === "document"
                ? "from document"
                : p.source_kind === "rebuild"
                  ? "rebuilt after review"
                  : "from library"}
              {p.site_name && (
                <span
                  className="beacon-library-tile-site"
                  data-testid={`pack-site-${p.pack_id}`}
                > · {p.site_name}</span>
              )}
              <span className="beacon-library-tile-site"
                data-testid={`pack-audience-${p.pack_id}`}
                title="The audience/context this pack was written for — Luma reviews it against this context">
                {" "}· {p.audience || "field"} audience
              </span>
              {p.review || p.rebuilt_from?.review ? (
                <ScoreBadge pack={p} />
              ) : null}
            </div>
            <button type="button"
              className="beacon-library-tile-title"
              data-testid={`pack-open-${p.pack_id}`}
              onClick={() => openPack(p.pack_id)}
              disabled={isTrash}
              title={isTrash ? "Restore to open" : undefined}>
              {p.title}
            </button>
            <div className="beacon-library-tile-theme">{p.theme_name}</div>
            {p.review?.verdict && (
              <div className="beacon-library-tile-verdict"
                data-testid={`pack-verdict-${p.pack_id}`}>
                &ldquo;{p.review.verdict}&rdquo;
              </div>
            )}
            <div className="beacon-library-tile-when">
              {isTrash && p.deleted_at ? (
                <>Deleted {new Date(p.deleted_at).toLocaleString()}</>
              ) : (
                <>{new Date(p.created_at).toLocaleString()}</>
              )}
            </div>
            {p.source_manifest && (
              <div className="beacon-library-tile-sources">
                {p.source_manifest.map((m, i) => (
                  <code key={`${m.name}-${i}`}>{m.name}</code>
                ))}
              </div>
            )}
            <div className="beacon-library-tile-actions">
              {isTrash ? (
                <>
                  <button type="button" className="beacon-tile-action"
                    data-testid={`pack-restore-${p.pack_id}`}
                    onClick={() => restorePack(p.pack_id)}
                    title="Restore">↺ restore</button>
                  <button type="button" className="beacon-tile-action is-danger"
                    data-testid={`pack-purge-${p.pack_id}`}
                    onClick={() => purgePack(p.pack_id)}
                    title="Permanently delete">✕ purge</button>
                </>
              ) : (
                <>
                  <button type="button" className="beacon-tile-action"
                    data-testid={`pack-share-${p.pack_id}`}
                    onClick={async () => {
                      const existing = sites.map((s) => s.site_name).filter(Boolean).join(", ");
                      const target = window.prompt(
                        `Copy "${p.title}" to which site/context?${existing ? `\nExisting sites: ${existing}` : ""}\nType a site name (new or existing):`,
                        "",
                      );
                      if (!target || !target.trim()) return;
                      try {
                        await axios.post(`${API}/packs/${p.pack_id}/duplicate`, {
                          site_name: target.trim(),
                        });
                        await load();
                      } catch (e) {
                        alert(`Copy failed: ${e.response?.data?.detail || e.message}`);
                      }
                    }}
                    title="Copy this pack to another site/context so it can be reused there">⧉ share</button>
                  <button type="button" className="beacon-tile-action"
                    data-testid={`pack-review-${p.pack_id}`}
                    onClick={() => onReviewPack?.({
                      kind: "pack", id: p.pack_id, title: p.title,
                    })}
                    title="Review with Luma">✦ review</button>
                  <button type="button" className="beacon-tile-action"
                    data-testid={`pack-fork-${p.pack_id}`}
                    onClick={() => setForking(p.pack_id)}
                    title="Fork to calendar">⑂ fork</button>
                  <button type="button" className="beacon-tile-action is-danger"
                    data-testid={`pack-delete-${p.pack_id}`}
                    onClick={() => deletePack(p.pack_id)}
                    title="Delete">🗑 delete</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {forking && (
        <ForkModal
          packId={forking}
          onClose={() => setForking(null)}
          onDone={async () => { setForking(null); await load(); }}
        />
      )}
    </section>
  );
}

function ForkModal({ packId, onClose, onDone }) {
  const [month, setMonth] = useState("January");
  const [week, setWeek] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const slot = `${month.slice(0, 3).toLowerCase()}-w${week}`;

  const fork = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/packs/${packId}/fork-to-calendar`, {
        slot, reason: reason.trim() || null,
      });
      onDone();
    } catch (e) {
      alert(`Fork failed: ${e.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="beacon-modal-backdrop" data-testid="fork-modal">
      <div className="beacon-modal">
        <div className="beacon-modal-head">
          <div className="beacon-tiny-label">FORK PACK TO CALENDAR</div>
          <button type="button" className="beacon-secondary-btn"
            data-testid="fork-modal-close" onClick={onClose}>Close</button>
        </div>
        <div className="beacon-field">
          <label className="beacon-label">Month</label>
          <select className="beacon-input" value={month}
            data-testid="fork-month"
            onChange={(e) => setMonth(e.target.value)}>
            {MONTH_ORDER.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="beacon-field">
          <label className="beacon-label">Week</label>
          <select className="beacon-input" value={week}
            data-testid="fork-week"
            onChange={(e) => setWeek(Number(e.target.value))}>
            {[1, 2, 3, 4].map((w) => <option key={w} value={w}>{`Week ${w}`}</option>)}
          </select>
        </div>
        <div className="beacon-field">
          <label className="beacon-label">Reason (optional)</label>
          <input className="beacon-input" value={reason}
            data-testid="fork-reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Site refresher post-incident"
            maxLength={240} />
        </div>
        <div className="beacon-slot-editor-actions">
          <button type="button" className="beacon-cta beacon-cta-compact"
            data-testid="fork-confirm"
            disabled={saving} onClick={fork}>
            {saving ? "Forking…" : `Fork into ${slot}`}
          </button>
        </div>
      </div>
    </div>
  );
}
